import { createContext, useContext, useState, useEffect, type ReactNode } from 'react';
import { type Company } from '@/types/settings';
import type { CompanyBasicInfoApi } from '@/types/api/user-context';

interface CompanyContextType {
  selectedCompany: Company | null;
  companies: Company[];
  setSelectedCompany: (company: Company) => void;
  isLoading: boolean;
  refreshExternalCompanies: () => Promise<void>;
}

const CompanyContext = createContext<CompanyContextType | undefined>(undefined);

// Mock companies data - used when running in 'app' host or demo mode
const mockCompanies: Company[] = [
  {
    id: '1',
    name: 'Acme Corporation',
    code: 'ACME',
    currency: 'GBP',
    fiscalYearStart: '2024-01-01',
    fiscalYearEnd: '2024-12-31',
    taxId: 'TAX-001',
    registrationDate: '2024-01-15',
    status: 'active',
    createdAt: '2024-01-15T10:00:00Z',
    organizationId: 'org-1',
  },
  {
    id: '2',
    name: 'Global Industries Ltd',
    code: 'GLOB',
    currency: 'EUR',
    fiscalYearStart: '2024-01-01',
    fiscalYearEnd: '2024-12-31',
    taxId: 'TAX-002',
    registrationDate: '2024-02-20',
    status: 'active',
    createdAt: '2024-02-20T14:30:00Z',
    organizationId: 'org-1',
  },
  {
    id: '3',
    name: 'Tech Innovations Inc',
    code: 'TECH',
    currency: 'USD',
    fiscalYearStart: '2024-01-01',
    fiscalYearEnd: '2024-12-31',
    taxId: 'TAX-003',
    registrationDate: '2024-03-10',
    status: 'active',
    createdAt: '2024-03-10T09:15:00Z',
    organizationId: 'org-1',
  },
  {
    id: '4',
    name: 'Retail Solutions Co',
    code: 'RETL',
    currency: 'GBP',
    fiscalYearStart: '2024-01-01',
    fiscalYearEnd: '2024-12-31',
    taxId: 'TAX-004',
    registrationDate: '2024-01-05',
    status: 'active',
    createdAt: '2024-01-05T11:45:00Z',
    organizationId: 'org-1',
  },
];

const STORAGE_KEY = 'selectedCompanyId';

/**
 * Convert CompanyBasicInfoApi from userContext to Company type
 * Only id and name are available from userContext, other fields use defaults
 */
function convertCompanyBasicInfoToCompany(companyInfo: CompanyBasicInfoApi): Company {
  return {
    id: String(companyInfo.id),
    name: companyInfo.name,
    code: '',
    currency: '',
    fiscalYearStart: '',
    fiscalYearEnd: '',
    taxId: '',
    registrationDate: '',
    status: 'active',
    createdAt: '',
    organizationId: '',
  };
}

export interface CompanyProviderProps {
  children: ReactNode;
  /** External companies from userContext (used when host is 'app-prod') */
  externalCompanies?: CompanyBasicInfoApi[];
  /** Whether external companies are still loading */
  externalCompaniesLoading?: boolean;
  /** Callback to refetch user context from the host app (e.g. after company creation) */
  refreshExternalCompanies?: () => Promise<void>;
}

export function CompanyProvider({
  children,
  externalCompanies,
  externalCompaniesLoading = false,
  refreshExternalCompanies,
}: CompanyProviderProps) {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [selectedCompany, setSelectedCompanyState] = useState<Company | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Determine if we should use external companies (from userContext in app-prod)
  const useExternalCompanies = externalCompanies !== undefined;

  // Load companies and restore selected company from sessionStorage
  // Using sessionStorage instead of localStorage for security - prevents cross-session persistence
  useEffect(() => {
    const loadCompanies = async () => {
      setIsLoading(true);
      try {
        let activeCompanies: Company[];

        if (useExternalCompanies) {
          // Use external companies from userContext (app-prod mode)
          if (externalCompaniesLoading) {
            // Still loading, wait for next effect trigger
            return;
          }
          activeCompanies = (externalCompanies || []).map(convertCompanyBasicInfoToCompany);
        } else {
          // Use mock data (app mode or standalone)
          await new Promise(resolve => setTimeout(resolve, 300));
          activeCompanies = mockCompanies.filter(c => c.status === 'active');
        }

        setCompanies(activeCompanies);

        // Restore selected company from sessionStorage
        const savedCompanyId = sessionStorage.getItem(STORAGE_KEY);
        if (savedCompanyId) {
          const savedCompany = activeCompanies.find(c => c.id === savedCompanyId);
          if (savedCompany) {
            setSelectedCompanyState(savedCompany);
          } else {
            // If saved company not found, select first company
            setSelectedCompanyState(activeCompanies[0] || null);
          }
        } else {
          // No saved company, select first one
          setSelectedCompanyState(activeCompanies[0] || null);
        }
      } catch (error) {
        console.error('Failed to load companies:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadCompanies();
  }, [useExternalCompanies, externalCompanies, externalCompaniesLoading]);

  const setSelectedCompany = (company: Company) => {
    setSelectedCompanyState(company);
    sessionStorage.setItem(STORAGE_KEY, company.id);
  };

  // Show loading state if external companies are still loading
  const effectiveIsLoading = isLoading || (useExternalCompanies && externalCompaniesLoading);

  return (
    <CompanyContext.Provider
      value={{
        selectedCompany,
        companies,
        setSelectedCompany,
        isLoading: effectiveIsLoading,
        refreshExternalCompanies: refreshExternalCompanies ?? (() => Promise.resolve()),
      }}
    >
      {children}
    </CompanyContext.Provider>
  );
}

export function useCompany() {
  const context = useContext(CompanyContext);
  if (context === undefined) {
    throw new Error('useCompany must be used within a CompanyProvider');
  }
  return context;
}
